/**
 * @file       Teachers vuex store, used by the whole teachers module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    register: {
      status: null,
      data: null,
      error: null
    },
    config: {
      status: null,
      data: null,
      error: null
    }
  };
}

/**
 * Teachers vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Register a teacher.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  register: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onRegister");

      axios
        .post(`teacher/register`, params)
        .then(response => {
          commit("onRegisterSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onRegisterError", error);
          reject(error);
        });
    });
  },

  /**
   * Get config.
   *
   * @param   {Function}  arg1.commit     Commit the action
   *
   * @return  {Promise}
   */
  getConfig: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit("onGetConfig");

      axios
        .get(`teacher/configuration`)
        .then(response => {
          commit("onGetConfigSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetConfigError", error);
          reject(error);
        });
    });
  },

  /**
   * Put config.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  putConfig: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPutConfig");

      axios
        .put(`teacher/configuration`, params)
        .then(response => {
          commit("onPutConfigSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutConfigError", error);
          reject(error);
        });
    });
  },

  /**
   * Post config.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postConfig: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostConfig");

      axios
        .post(`teacher/config`, payload)
        .then(response => {
          commit("onPostConfigSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostConfigError", error);
          reject(error);
        });
    });
  },

  /**
   * Delete config.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  deleteConfig: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onDeleteConfig");

      axios
        .delete(`teacher/config/${params.configRuleID}`)
        .then(response => {
          commit("onDeleteConfigSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onDeleteConfigError", error);
          reject(error);
        });
    });
  }
};

/**
 * Teachers vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Register.
   *
   * @param  {Object}  state   The vuex state
   */
  onRegister: state => {
    state.register.status = "fetching";
    state.register.error = {};
  },

  /**
   * Register success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onRegisterSuccess: (state, { data }) => {
    state.register.status = "success";
    state.register.error = null;
    state.register.data = data;
  },

  /**
   * Register error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onRegisterError: (state, err) => {
    state.register.status = "error";
    state.register.error = err;
  },

  /**
   * Get config.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetConfig: state => {
    state.config.status = "fetching";
    state.config.error = {};
  },

  /**
   * Get config success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetConfigSuccess: (state, { data }) => {
    state.config.status = "success";
    state.config.error = null;
    state.config.data = data;
  },

  /**
   * Get config error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetConfigError: (state, err) => {
    state.config.status = "error";
    state.config.error = err;
  },

  /**
   * Put config.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutConfig: state => {
    state.config.status = "fetching";
    state.config.error = {};
  },

  /**
   * Put config success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutConfigSuccess: (state, { data }) => {
    state.config.status = "success";
    state.config.error = null;
    state.config.data = data;
  },

  /**
   * Put config error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutConfigError: (state, err) => {
    state.config.status = "error";
    state.config.error = err;
  },

  /**
   * Post config.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostConfig: state => {
    state.config.status = "fetching";
    state.config.error = {};
  },

  /**
   * Post config success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostConfigSuccess: (state, { data }) => {
    state.config.status = "success";
    state.config.error = null;
    state.config.data = data;
  },

  /**
   * Post config error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostConfigError: (state, err) => {
    state.config.status = "error";
    state.config.error = err;
  },

  /**
   * Delete config.
   *
   * @param  {Object}  state   The vuex state
   */
  onDeleteConfig: state => {
    state.config.status = "fetching";
    state.config.error = {};
  },

  /**
   * Delete condif success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onDeleteConfigSuccess: (state, { data }) => {
    state.config.status = "success";
    state.config.error = null;
    state.config.data = data;
  },

  /**
   * Delete config error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onDeleteConfigError: (state, err) => {
    state.config.status = "error";
    state.config.error = err;
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
